import type { Schemas } from '@argzombie/api-client';

/*
 * Trigger code helpers for the Trigger codes page (ADR 0029 §12): channels, the batch CSV, QR images and the A4 poster.
 * Pure where possible so they're tested without a browser.
 */

/** The channel kinds a code can have (`trigger_codes.channel`). `card` is what batches use. */
export const CHANNEL_KINDS = ['web', 'admin', 'poster', 'event', 'card'] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];

/** A channel name after `poster:` / `event:` / `card:` (the API's rule). */
export const CHANNEL_NAME = /^[a-z0-9_-]{1,48}$/;

/** `web` and `admin` stand alone; the others carry a name. */
export const channelHasName = (kind: ChannelKind) => kind !== 'web' && kind !== 'admin';

export const channelOf = (kind: ChannelKind, name: string) =>
  channelHasName(kind) ? `${kind}:${name.trim()}` : kind;

/** Redemption statuses (`trigger_redemptions.status`). */
export const REDEMPTION_STATUSES = [
  'granted',
  'encounter_issued',
  'encounter_closed',
  'mission_unlocked',
  'event_started',
  'void',
] as const;

/** Code states the API reports and filters by. */
export const CODE_STATES = ['active', 'not_yet', 'used_up', 'retired'] as const;

const csvCell = (v: string | number | null | undefined) => {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Rows as CSV (RFC 4180 quoting, CRLF line ends, a header row). */
export function toCsv(header: string[], rows: (string | number | null | undefined)[][]): string {
  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

/** The printable list of a batch's cards: one row per code with its scan URL. */
export function batchCsv(batch: {
  batchId: string;
  channel: string;
  triggerId: string;
  codes: string[];
  scanUrls: string[];
}): string {
  return toCsv(
    ['code', 'scan_url', 'trigger_id', 'channel', 'batch_id'],
    batch.codes.map((code, i) => [
      code,
      batch.scanUrls[i] ?? '',
      batch.triggerId,
      batch.channel,
      batch.batchId,
    ]),
  );
}

/** Saves a blob as a download. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const QR_OPTIONS = {
  errorCorrectionLevel: 'H' as const,
  margin: 4,
  color: { dark: '#15121aff', light: '#ffffffff' },
};

/** A print-ready QR SVG of a URL (ECC H, quiet zone 4, ink on white). `qrcode` loads on first use. */
export async function qrSvg(url: string): Promise<string> {
  const { default: QRCode } = await import('qrcode');
  return QRCode.toString(url, { ...QR_OPTIONS, type: 'svg' });
}

/** The same QR as a 1024 px PNG data URL. */
export async function qrPngDataUrl(url: string): Promise<string> {
  const { default: QRCode } = await import('qrcode');
  return QRCode.toDataURL(url, { ...QR_OPTIONS, type: 'image/png', width: 1024 });
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * A standalone A4 poster page (print it, or save as PDF): the title, the description, the QR, the code under it and the
 * URL, in the site's ink-on-newsprint look. `svg` is `qrSvg(scanUrl)`.
 */
export function posterHtml(p: {
  title: string;
  description?: string | null;
  code: string;
  scanUrl: string;
  svg: string;
}): string {
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${esc(p.title)} · ${esc(p.code)}</title>
<style>
@page { size: A4; margin: 14mm; }
* { box-sizing: border-box; }
html, body { margin: 0; background: #fff; color: #15121a; font-family: 'Courier Prime', 'Courier New', monospace; }
.page { width: 182mm; min-height: 265mm; margin: 0 auto; border: 3px solid #15121a; padding: 12mm; display: flex; flex-direction: column; align-items: center; text-align: center; }
.kicker { font-size: 14pt; letter-spacing: 0.3em; text-transform: uppercase; }
h1 { font-family: Impact, 'Bangers', sans-serif; font-size: 44pt; margin: 4mm 0; line-height: 1; text-transform: uppercase; }
.desc { font-size: 13pt; max-width: 150mm; margin: 0 0 8mm; }
.qr { width: 120mm; height: 120mm; }
.qr svg { width: 100%; height: 100%; }
.code { font-size: 22pt; font-weight: bold; letter-spacing: 0.08em; margin-top: 4mm; }
.url { font-size: 10pt; margin-top: 2mm; word-break: break-all; }
.how { margin-top: auto; font-size: 12pt; border-top: 2px solid #15121a; padding-top: 4mm; width: 100%; }
</style></head>
<body><div class="page">
<div class="kicker">ARG Zombie</div>
<h1>${esc(p.title)}</h1>
${p.description ? `<p class="desc">${esc(p.description)}</p>` : ''}
<div class="qr">${p.svg}</div>
<div class="code">${esc(p.code)}</div>
<div class="url">${esc(p.scanUrl)}</div>
<div class="how">Scan with the ARG Zombie app (SCAN on the map), or enter the code. Sign in to play.</div>
</div></body></html>
`;
}

/** A one-line summary of what the redemption gave (from the stored result). */
export function resultSummary(r: Schemas['AdminTriggerRedemptionRow']): string {
  const res = r.result;
  if (!res) return '';
  const g = res.grants;
  const parts: string[] = [];
  if (g) {
    if (g.items?.length)
      parts.push(
        `${g.items.map((s) => `${s.quantity}× ${s.itemId}`).join(', ')}${g.destination ? ` → ${g.destination}` : ''}`,
      );
    if (g.health) parts.push(`heal ${g.health.target} (${g.health.changes.length})`);
    if (g.currency) parts.push(`+${g.currency.amount} ${g.currency.kind}`);
    if (g.cosmetics?.length) parts.push(`cosmetic ${g.cosmetics.join(', ')}`);
    if (g.alreadyOwned?.length) parts.push(`already owned ${g.alreadyOwned.join(', ')}`);
  }
  if (res.encounter)
    parts.push(
      `${res.encounter.ticket.mode ?? 'raid'} ticket ${res.encounter.ticket.raidId ?? ''}`.trim(),
    );
  if (res.mission) parts.push(`mission ${res.mission.id}`);
  if (res.event) parts.push(`${res.event.type} at ${res.event.buildingName}`);
  return parts.join(' · ') || (res.message ?? '');
}
